import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCreatePromoterMutation, AddPromoterRequest } from '../promoter.service';
import { FaArrowLeft, FaSave, FaUser, FaEnvelope, FaPercent, FaInfoCircle, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
// @ts-ignore - sweetalert2 has no types in current setup
import Swal from 'sweetalert2';
import { appendSearchToPath } from 'src/customHooks/usePaginatedTableSearchParams';

export const CreatePromoterForm: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const backToPromoters = appendSearchToPath('/promoters', location.search);
  const [createPromoter, { isLoading }] = useCreatePromoterMutation();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    commissionRate: 10, // 10% default (stored as whole number)
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  // Animation on mount
  useEffect(() => {
    setAnimateIn(true);
  }, []);

  const validateFormSilent = useCallback(() => {
    return (
      formData.name.trim().length > 0 &&
      (formData.email === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) &&
      formData.commissionRate >= 0 &&
      formData.commissionRate <= 100
    );
  }, [formData.commissionRate, formData.email, formData.name]);

  // Real-time validation
  useEffect(() => {
    const isValid = validateFormSilent();
    setIsFormValid(isValid);
  }, [validateFormSilent]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters long';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.commissionRate < 0 || formData.commissionRate > 100) {
      newErrors.commissionRate = 'Commission rate must be between 0% and 100%';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const request: AddPromoterRequest = {
        name: formData.name.trim(),
        email: formData.email.trim() || undefined,
        commissionRate: formData.commissionRate, // Convert percentage to decimal for storage
      };

      const result = await createPromoter(request).unwrap();

      if (result.success) {
        Swal.fire({
          title: 'Success! 🎉',
          text: 'Promoter has been created successfully.',
          icon: 'success',
          confirmButtonColor: '#10b981',
          showClass: {
            popup: 'animate__animated animate__fadeInDown'
          },
          hideClass: {
            popup: 'animate__animated animate__fadeOutUp'
          }
        }).then(() => {
          navigate(backToPromoters);
        });
      } else {
        // Handle error from the service
        Swal.fire({
          title: 'Error! ❌',
          text: result.error || 'Failed to create promoter. Please try again.',
          icon: 'error',
          confirmButtonColor: '#ef4444',
          showClass: {
            popup: 'animate__animated animate__fadeInDown'
          }
        });
      }
    } catch (error) {
      console.error('Error creating promoter:', error);
      Swal.fire({
        title: 'Error! ❌',
        text: 'Failed to create promoter. Please try again.',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        showClass: {
          popup: 'animate__animated animate__fadeInDown'
        }
      });
    }
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Real-time email validation
    if (field === 'email' && typeof value === 'string') {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
      } else if (value) {
        setErrors(prev => ({ ...prev, email: '' }));
      }
    }
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleCommissionRateChange = (value: number) => {
    // Round to whole number for percentage
    const roundedValue = Math.round(value);
    handleInputChange('commissionRate', roundedValue);
  };

  const formatCommissionRate = (rate: number) => {
    return `${Math.round(rate)}%`;
  };

  const getFieldStatus = (field: string) => {
    if (errors[field] && touched[field]) {
      return 'error';
    }
    if (touched[field] && formData[field as keyof typeof formData]) {
      return 'success';
    }
    return 'default';
  };

  const getFieldIcon = (field: string) => {
    const status = getFieldStatus(field);
    switch (status) {
      case 'error':
        return <FaExclamationTriangle className="h-5 w-5 text-red-500" />;
      case 'success':
        return <FaCheckCircle className="h-5 w-5 text-green-500" />;
      default:
        return null;
    }
  };

  const getFieldBorderColor = (field: string) => {
    const status = getFieldStatus(field);
    switch (status) {
      case 'error':
        return 'border-red-300 focus:ring-red-500 focus:border-red-500';
      case 'success':
        return 'border-green-300 focus:ring-green-500 focus:border-green-500';
      default:
        return 'border-gray-300 focus:ring-emerald-500 focus:border-emerald-500';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          {/* Enhanced Header with Animation */}
          <div className={`mb-10 transition-all duration-1000 ease-out transform ${
            animateIn ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}>
            <button
              onClick={() => navigate(backToPromoters)}
              className="flex items-center text-emerald-600 hover:text-emerald-700 font-medium mb-6 transition-all duration-300 group hover:scale-105"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl mr-3 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <FaArrowLeft className="text-white" />
              </div>
              <span className="text-lg">Back to Promoters</span>
            </button>
            
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8 border border-gray-100 hover:shadow-3xl transition-all duration-500">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl mb-6 flex items-center justify-center">
                  <FaUser className="text-white text-3xl" />
                </div>
                <h1 className="text-5xl font-bold text-gray-900 mb-4">Create New Promoter</h1>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                  Add a new promoter to the system with their personal and financial information
                </p>
              </div>
            </div>
          </div>

          {/* Enhanced Form with Staggered Animation */}
          <div className={`bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8 border border-gray-100 transition-all duration-1000 ease-out transform ${
            animateIn ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}>
            <form onSubmit={handleSubmit} className="space-y-10">
              {/* Personal Information */}
              <div className="space-y-6 animate-fade-in-up">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl mr-4 flex items-center justify-center shadow-lg">
                    <FaUser className="text-white text-lg" />
                  </div>
                  Personal Information
                </h3>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="space-y-4 animate-fade-in-left">
                    <label className="text-sm font-medium text-gray-700 uppercase tracking-wide flex items-center gap-2">
                      Full Name *
                      <FaInfoCircle className="text-gray-400 text-xs" title="Enter the promoter's full name" />
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-300 group-focus-within:text-emerald-500">
                        <FaUser className="h-5 w-5" />
                      </div>
                                             <input
                         type="text"
                         value={formData.name}
                         onChange={(e) => handleInputChange('name', e.target.value)}
                         onBlur={() => handleBlur('name')}
                         className={`block w-full pl-12 pr-12 py-4 border-2 rounded-2xl leading-5 bg-gradient-to-r from-white to-gray-50 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-offset-2 text-lg font-medium transition-all duration-500 hover:shadow-lg hover:from-gray-50 hover:to-white transform hover:scale-[1.02] ${getFieldBorderColor('name')}`}
                         placeholder="Enter promoter's full name"
                         autoFocus
                       />
                      <div className="absolute inset-y-0 right-0 pr-4 flex items-center transition-all duration-300">
                        {getFieldIcon('name')}
                      </div>
                    </div>
                    {errors.name && touched.name && (
                      <p className="text-red-600 text-sm mt-2 flex items-center gap-2 animate-fade-in">
                        <FaExclamationTriangle className="text-xs" />
                        {errors.name}
                      </p>
                    )}
                    {!errors.name && touched.name && formData.name && (
                      <p className="text-green-600 text-sm mt-2 flex items-center gap-2 animate-fade-in">
                        <FaCheckCircle className="text-xs" />
                        Name looks good!
                      </p>
                    )}
                  </div>

                  <div className="space-y-4 animate-fade-in-right">
                    <label className="text-sm font-medium text-gray-700 uppercase tracking-wide flex items-center gap-2">
                      Email Address
                      <FaInfoCircle className="text-gray-400 text-xs" title="Optional email for communication" />
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-300 group-focus-within:text-emerald-500">
                        <FaEnvelope className="h-5 w-5" />
                      </div>
                                             <input
                         type="email"
                         value={formData.email}
                         onChange={(e) => handleInputChange('email', e.target.value)}
                         onBlur={() => handleBlur('email')}
                         className={`block w-full pl-12 pr-12 py-4 border-2 rounded-2xl leading-5 bg-gradient-to-r from-white to-gray-50 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-offset-2 text-lg font-medium transition-all duration-500 hover:shadow-lg hover:from-gray-50 hover:to-white transform hover:scale-[1.02] ${getFieldBorderColor('email')}`}
                         placeholder="Enter email address (optional)"
                       />
                      <div className="absolute inset-y-0 right-0 pr-4 flex items-center transition-all duration-300">
                        {getFieldIcon('email')}
                      </div>
                    </div>
                                         {errors.email && touched.email && (
                       <p className="text-red-600 text-sm mt-2 flex items-center gap-2 animate-fade-in">
                         <FaExclamationTriangle className="text-xs" />
                         {errors.email}
                       </p>
                     )}
                     {!errors.email && touched.email && formData.email && (
                       <p className="text-green-600 text-sm mt-2 flex items-center gap-2 animate-fade-in">
                           <FaCheckCircle className="text-xs" />
                           Valid email format!
                       </p>
                     )}
                  </div>
                </div>
              </div>

              {/* Financial Information */}
              <div className="space-y-6 animate-fade-in-up-delay">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl mr-4 flex items-center justify-center shadow-lg">
                    <FaPercent className="text-white text-lg" />
                  </div>
                  Financial Information
                </h3>
                
                <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">
                  <div className="space-y-4 animate-fade-in-up">
                                         <label className="text-sm font-medium text-gray-700 uppercase tracking-wide flex items-center gap-2">
                       Commission Rate *
                       <FaInfoCircle className="text-gray-400 text-xs" title="Commission rate as a percentage (10 = 10%)" />
                     </label>
                    
                    {/* Commission Rate Slider */}
                    <div className="space-y-6">
                                             <div className="relative group">
                         <input
                           type="range"
                           min="0"
                           max="100"
                           step="1"
                           value={formData.commissionRate}
                           onChange={(e) => handleCommissionRateChange(parseFloat(e.target.value))}
                           onBlur={() => handleBlur('commissionRate')}
                           className="w-full h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded-2xl appearance-none cursor-pointer range-slider transition-all duration-500 hover:from-emerald-200 hover:to-emerald-300 focus:outline-none focus:ring-4 focus:ring-emerald-300 focus:ring-offset-2 transform hover:scale-y-110"
                         />
                        <div className="flex justify-between text-xs text-gray-500 mt-2">
                          <span className="transition-all duration-300 hover:text-gray-700">0%</span>
                          <span className="transition-all duration-300 hover:text-gray-700">25%</span>
                          <span className="transition-all duration-300 hover:text-gray-700">50%</span>
                          <span className="transition-all duration-300 hover:text-gray-700">75%</span>
                          <span className="transition-all duration-300 hover:text-gray-700">100%</span>
                        </div>
                      </div>
                      
                                             {/* Commission Rate Display */}
                       <div className="text-center animate-bounce-in">
                         <div className="inline-flex items-center px-10 py-6 bg-gradient-to-br from-emerald-100 via-teal-100 to-blue-100 rounded-3xl border-2 border-emerald-300 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:scale-110 transform group">
                           <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl mr-4 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                             <FaPercent className="text-white text-xl" />
                           </div>
                           <div className="text-center">
                             <div className="text-4xl font-black text-emerald-800 group-hover:text-emerald-900 transition-colors duration-300">
                               {formatCommissionRate(formData.commissionRate)}
                             </div>
                             <div className="text-sm text-emerald-600 font-medium mt-1">Commission Rate</div>
                           </div>
                         </div>
                       </div>
                      
                      
                    </div>
                    
                    {errors.commissionRate && touched.commissionRate && (
                      <p className="text-red-600 text-sm mt-2 flex items-center gap-2 animate-fade-in">
                        <FaExclamationTriangle className="text-xs" />
                        {errors.commissionRate}
                      </p>
                    )}
                    
                                         <div className="text-sm text-gray-500 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200 hover:shadow-md transition-all duration-300">
                       💡 <strong>Tip:</strong> Use the slider above to set the commission rate. 
                       The value will be automatically converted to a percentage.
                     </div>
                  </div>
                </div>
              </div>

                             {/* Form Summary */}
               <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-blue-50 rounded-3xl p-8 border-2 border-emerald-200 animate-fade-in-up-delay-2 hover:shadow-2xl transition-all duration-500 transform hover:scale-105">
                 <h4 className="text-2xl font-bold text-emerald-800 mb-6 flex items-center gap-3">
                   <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                     <FaInfoCircle className="text-white text-lg animate-pulse" />
                   </div>
                   Form Summary
                 </h4>
                 <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                   <div className="text-center p-6 bg-gradient-to-br from-white to-gray-50 rounded-2xl border-2 border-gray-200 hover:shadow-xl transition-all duration-500 hover:scale-110 transform group">
                     <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl mx-auto mb-3 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                       <FaUser className="text-white text-lg" />
                     </div>
                     <p className="text-gray-600 font-medium mb-2">Name</p>
                     <p className="font-bold text-gray-800 text-lg">{formData.name || 'Not provided'}</p>
                   </div>
                   <div className="text-center p-6 bg-gradient-to-br from-white to-gray-50 rounded-2xl border-2 border-gray-200 hover:shadow-xl transition-all duration-500 hover:scale-110 transform group">
                     <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl mx-auto mb-3 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                       <FaEnvelope className="text-white text-lg" />
                     </div>
                     <p className="text-gray-600 font-medium mb-2">Email</p>
                     <p className="font-bold text-gray-800 text-lg">{formData.email || 'Not provided'}</p>
                   </div>
                   <div className="text-center p-6 bg-gradient-to-br from-white to-gray-50 rounded-2xl border-2 border-gray-200 hover:shadow-xl transition-all duration-500 hover:scale-110 transform group">
                     <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl mx-auto mb-3 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                       <FaPercent className="text-white text-lg" />
                     </div>
                     <p className="text-gray-600 font-medium mb-2">Commission</p>
                     <p className="font-bold text-emerald-800 text-xl">{formatCommissionRate(formData.commissionRate)}</p>
                   </div>
                 </div>
               </div>

              {/* Enhanced Submit Button */}
              <div className="flex flex-col sm:flex-row justify-between items-center pt-8 border-t border-gray-200 animate-fade-in-up-delay-3">
                <div className="mb-4 sm:mb-0">
                  <p className="text-sm text-gray-600">
                    {isFormValid ? (
                      <span className="text-green-600 flex items-center gap-2 animate-fade-in">
                        <FaCheckCircle className="text-sm" />
                        All fields are valid ✓
                      </span>
                    ) : (
                      <span className="text-orange-600 flex items-center gap-2 animate-fade-in">
                        <FaExclamationTriangle className="text-sm" />
                        Please complete all required fields
                      </span>
                    )}
                  </p>
                </div>
                
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => navigate(backToPromoters)}
                    className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-xl shadow-md hover:shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 hover:scale-105"
                  >
                    Cancel
                  </button>
                  
                  <button
                    type="submit"
                    disabled={isLoading || !isFormValid}
                    className={`px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-medium rounded-2xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-2 flex items-center gap-3 ${
                      isLoading || !isFormValid ? 'opacity-50 cursor-not-allowed transform-none' : 'hover:scale-105'
                    }`}
                  >
                    <FaSave className="text-lg" />
                    {isLoading ? 'Creating Promoter...' : 'Create Promoter'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes fadeInLeft {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes fadeInRight {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes bounceIn {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.05); }
          70% { transform: scale(0.9); }
          100% { transform: scale(1); opacity: 1; }
        }
        
        .animate-fade-in {
          animation: fadeIn 0.6s ease-out;
        }
        
        .animate-fade-in-up {
          animation: fadeIn 0.8s ease-out;
        }
        
        .animate-fade-in-up-delay {
          animation: fadeIn 0.8s ease-out 0.2s both;
        }
        
        .animate-fade-in-up-delay-2 {
          animation: fadeIn 0.8s ease-out 0.4s both;
        }
        
        .animate-fade-in-up-delay-3 {
          animation: fadeIn 0.8s ease-out 0.6s both;
        }
        
        .animate-fade-in-left {
          animation: fadeInLeft 0.8s ease-out;
        }
        
        .animate-fade-in-right {
          animation: fadeInRight 0.8s ease-out;
        }
        
        .animate-bounce-in {
          animation: bounceIn 0.8s ease-out;
        }
        
                 .range-slider::-webkit-slider-thumb {
           appearance: none;
           height: 28px;
           width: 28px;
           border-radius: 50%;
           background: linear-gradient(135deg, #10b981, #059669, #0d9488);
           cursor: pointer;
           box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4), 0 2px 4px rgba(0, 0, 0, 0.1);
           border: 3px solid white;
           transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
         }
         
         .range-slider::-webkit-slider-thumb:hover {
           transform: scale(1.3);
           box-shadow: 0 8px 25px rgba(16, 185, 129, 0.6), 0 4px 12px rgba(0, 0, 0, 0.15);
           background: linear-gradient(135deg, #059669, #047857, #065f46);
         }
         
         .range-slider::-webkit-slider-thumb:active {
           transform: scale(1.1);
           box-shadow: 0 6px 20px rgba(16, 185, 129, 0.8), 0 2px 8px rgba(0, 0, 0, 0.2);
         }
         
         .range-slider::-moz-range-thumb {
           height: 28px;
           width: 28px;
           border-radius: 50%;
           background: linear-gradient(135deg, #10b981, #059669, #0d9488);
           cursor: pointer;
           border: 3px solid white;
           box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4), 0 2px 4px rgba(0, 0, 0, 0.1);
           transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
         }
         
         .range-slider::-moz-range-thumb:hover {
           transform: scale(1.3);
           box-shadow: 0 8px 25px rgba(16, 185, 129, 0.6), 0 4px 12px rgba(0, 0, 0, 0.15);
           background: linear-gradient(135deg, #059669, #047857, #065f46);
         }
         
         .range-slider::-moz-range-thumb:active {
           transform: scale(1.1);
           box-shadow: 0 6px 20px rgba(16, 185, 129, 0.8), 0 2px 8px rgba(0, 0, 0, 0.2);
         }
      `}</style>
    </div>
  );
};
